import { getMomentDate } from '../../../Utils/dates.util';
import { buildScheduleItem } from '../../../Components/Calendar/calendar.helper';
import apiClient from '../../../Services/ApiClient';
import { getFormattedPriceAsFloat } from '../../Base/Services/priceFormat.service';
import useNotification from '../../../Hooks/useNotification.hook';
import { ServiceScheduleShiftFormValues } from '../Components/ServiceScheduleShiftForm/ServiceScheduleShiftForm';
import moment from 'moment';
import { useAppContext } from '../../../Services/AppContext';
import useTranslation from '../../../Hooks/useTranslation';
import useApiClient from '../../Base/Hooks/useApiClient';
import { calculateDatesAndTimesForServiceSchedule } from '../helpers/serviceScheduleDateTime.helper';
import { RepeatInterval } from '../Enums/RepeatInterval.enum';

export default function useServiceSchedulesHelper() {
	const { showNotificationSuccess } = useNotification();
	const { currency, vatRate } = useAppContext();
	const { t } = useTranslation();
	const { displayError } = useApiClient();

	// HELPING FC FOR PREPARE CORRECT DATE AND TIME VALUES FOR SERVICE SCHEDULE CREAT OR UPDATE
	const calculateTimesFromValues = (values: ServiceScheduleShiftFormValues) => {
		const { dateTo, dateFrom, timeTo, timeFrom } =
			calculateDatesAndTimesForServiceSchedule({
				date: values.date,
				timeFrom: values.timeFrom,
				timeTo: values.timeTo,
			});

		return { dateTo, dateFrom, timeTo, timeFrom };
	};

	///FC TO CREATE NEW SHIFT SERVICE SCHEDULE
	const createServiceSchedule = async ({
		workplaceId,
		values,
	}: {
		workplaceId: string;
		values: ServiceScheduleShiftFormValues;
	}) => {
		values.date = getMomentDate(values.date);

		const { dateFrom, dateTo, timeFrom, timeTo } =
			calculateTimesFromValues(values);

		try {
			if (values.workingTime) {
				const serviceSchedule = await apiClient.post(
					'/workplaces/' + workplaceId + '/services-schedule/',
					{
						dateFrom: dateFrom,
						dateTo: dateTo,
						form: 'standard',
						type: values.workingTime.toLowerCase(),
					},
				);

				if (serviceSchedule && timeTo && timeFrom) {
					const scheduleId = serviceSchedule.data.id;
					const preparedObject = buildScheduleItem({
						...values,
						workplaceMembers: [values.workplaceMember],
						timeTo,
						timeFrom,
						price:
							values.price && values.extraPayment
								? {
										priceWithVat: getFormattedPriceAsFloat(values.price),
										currency,
										vatRate,
								  }
								: undefined,
					});
					await apiClient.post(
						'/workplaces/' +
							workplaceId +
							'/services-schedule/' +
							scheduleId +
							'/items/',
						preparedObject,
					);
					showNotificationSuccess({
						content: t('component.workplaceSettings.scheduleEventCreated'),
					});

					return true;
				}
			}
		} catch (e) {
			displayError(e);
		}
	};

	///FC TO UPDATE SHIFT SERVICE SCHEDULE
	const updateServiceSchedule = async ({
		workplaceId,
		values,
		updateFuture,
		serviceScheduleId,
		serviceScheduleItemId,
		excludeDate,
	}: {
		workplaceId: string;
		values: ServiceScheduleShiftFormValues;
		updateFuture: boolean;
		serviceScheduleId: string;
		serviceScheduleItemId: string;
		excludeDate?: string;
	}) => {
		values.date = getMomentDate(values.date);

		const { dateFrom, dateTo, timeFrom, timeTo } =
			calculateTimesFromValues(values);

		const days: Record<string, string> = {
			Monday: 'mon',
			Tuesday: 'tue',
			Wednesday: 'wed',
			Thursday: 'thu',
			Friday: 'fri',
			Saturday: 'sat',
			Sunday: 'sun',
		};

		const membersToSave: string[] = [values.workplaceMember.value as string];

		const updateData: {
			day: string;
			timeFrom: string;
			timeTo: string;
			workplaceMemberIds: string[];
			price: {
				priceWithVat: number;
				currency: string;
				vatRate: number;
			} | null;
			interval?: number;
			dateFrom: string;
			dateTo: string;
			updateFuture: boolean;
			repeatInterval?: string;
			repeatTo?: string;
			specificDaysInWeek?: {
				mon: boolean;
				tue: boolean;
				wed: boolean;
				thu: boolean;
				fri: boolean;
				sat: boolean;
				sun: boolean;
			};
			canOrderImmediate: boolean;
			maxActiveCases?: number | null;
			excludeDate?: string;
		} = {
			day: days[values.date.format('dddd')],
			timeFrom: moment(timeFrom).format('HH:mmZ'),
			timeTo: moment(timeTo).format('HH:mmZ'),
			workplaceMemberIds: membersToSave,
			price:
				values.price && values.extraPayment
					? {
							priceWithVat: getFormattedPriceAsFloat(values.price),
							currency,
							vatRate,
					  }
					: null,
			interval: values.guaranteedTime,
			dateFrom: dateFrom,
			dateTo: dateTo,
			canOrderImmediate: true,
			maxActiveCases: values.limitMaxActiveCases ? values.maxActiveCases : null,
			updateFuture,
		};

		if (excludeDate) {
			updateData.excludeDate = excludeDate;
		}

		if (
			!updateFuture &&
			values.repeatInterval &&
			values.repeatInterval.value !== 'never' &&
			values.repeatTo
		) {
			updateData.repeatInterval = values.repeatInterval.value as string;
			updateData.repeatTo = getMomentDate(values.repeatTo).format('YYYY-MM-DD');
			if (
				values.repeatInterval.value === RepeatInterval.SPECIFIC_DAYS_IN_WEEK &&
				values.specificDaysInWeek
			) {
				updateData.specificDaysInWeek = values.specificDaysInWeek;
			}
		}

		try {
			await apiClient.patch(
				`/workplaces/${workplaceId}/services-schedule/${serviceScheduleId}/items/${serviceScheduleItemId}`,
				updateData,
			);
			return true;
		} catch (e) {
			displayError(e);
		}
	};

	return { createServiceSchedule, updateServiceSchedule };
}
